/**
 * Hooks for the Account settings page (Phase E).
 *
 * Endpoints under `/api/v1/auth/me/*` — see `AuthMeController` on the
 * backend. All require an authenticated caller. Mutations invalidate
 * the relevant React Query keys so the page reflects server state
 * without manual refetch.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { authFetch, authFetchJson } from "./auth-fetch";
import { throwIfNotOk } from "./api-error";
import { useAuthStore } from "./auth";

/* ====================== Password change ====================== */

export type ChangePasswordInput = {
  currentPassword: string;
  newPassword: string;
};

export function useChangePassword() {
  return useMutation({
    mutationFn: async (body: ChangePasswordInput) => {
      const res = await authFetch(`/api/v1/auth/me/password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const reply = await res.json().catch(() => ({}));
        throw new Error(reply.detail ?? reply.message ?? `HTTP ${res.status}`);
      }
    },
  });
}

/* ====================== Email change ====================== */

export type RequestEmailChangeInput = {
  newEmail: string;
  password: string;
};

export type PendingEmailChange = {
  newEmail: string;
  expiresAt: string;
};

export function usePendingEmailChange() {
  const accessToken = useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ["auth", "me", "email", "pending"],
    enabled: !!accessToken,
    queryFn: async () => {
      // The endpoint returns null when no pending change exists. authFetchJson
      // happily decodes that (Jackson serializes null as "null" the body).
      const res = await authFetch(`/api/v1/auth/me/email/pending-change`);
      await throwIfNotOk(res);
      const text = await res.text();
      if (!text) return null;
      return JSON.parse(text) as PendingEmailChange | null;
    },
  });
}

export function useRequestEmailChange() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: RequestEmailChangeInput) => {
      const res = await authFetch(`/api/v1/auth/me/email/change`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const reply = await res.json().catch(() => ({}));
        throw new Error(reply.detail ?? reply.message ?? `HTTP ${res.status}`);
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["auth", "me", "email", "pending"] }),
  });
}

export function useCancelEmailChange() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await authFetch(`/api/v1/auth/me/email/pending-change`, {
        method: "DELETE",
      });
      await throwIfNotOk(res);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["auth", "me", "email", "pending"] }),
  });
}

/* ====================== Two-factor (TOTP) ====================== */

export type TwoFactorSetup = {
  secret: string;
  otpauthUri: string;
  qrPngDataUrl: string;
};

export function useStartTwoFactorSetup() {
  return useMutation({
    mutationFn: () =>
      authFetchJson<TwoFactorSetup>(`/api/v1/auth/me/2fa/setup`, {
        method: "POST",
      }),
  });
}

export function useConfirmTwoFactorSetup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (code: string) =>
      authFetchJson<{ recoveryCodes: string[] }>(`/api/v1/auth/me/2fa/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["user", "me"] }),
  });
}

export function useDisableTwoFactor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (password: string) => {
      const res = await authFetch(`/api/v1/auth/me/2fa/disable`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) {
        const reply = await res.json().catch(() => ({}));
        throw new Error(reply.detail ?? reply.message ?? `HTTP ${res.status}`);
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["user", "me"] }),
  });
}

export function useRegenerateRecoveryCodes() {
  return useMutation({
    mutationFn: (password: string) =>
      authFetchJson<{ recoveryCodes: string[] }>(
        `/api/v1/auth/me/2fa/recovery/regenerate`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ password }),
        },
      ),
  });
}

/* ====================== Sessions ====================== */

export type Session = {
  id: string;
  ip: string | null;
  userAgent: string | null;
  issuedAt: string;
  expiresAt: string;
  current: boolean;
};

export function useSessions() {
  const accessToken = useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ["auth", "me", "sessions"],
    enabled: !!accessToken,
    queryFn: () =>
      authFetchJson<{ sessions: Session[] }>(`/api/v1/auth/me/sessions`),
  });
}

export function useRevokeSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await authFetch(`/api/v1/auth/me/sessions/${id}`, {
        method: "DELETE",
      });
      await throwIfNotOk(res);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["auth", "me", "sessions"] }),
  });
}

export function useRevokeOtherSessions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await authFetch(`/api/v1/auth/me/sessions/revoke-others`, {
        method: "POST",
      });
      await throwIfNotOk(res);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["auth", "me", "sessions"] }),
  });
}

/* ====================== Preferences ====================== */

export type PreferencesInput = {
  locale?: string | null;
  timezone?: string | null;
};

export type Preferences = {
  locale: string | null;
  timezone: string | null;
};

export function useUpdatePreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PreferencesInput) =>
      authFetchJson<Preferences>(`/api/v1/auth/me/preferences`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["user", "me"] }),
  });
}

/* ====================== Account deletion ====================== */

export type DeleteAccountInput = {
  password: string;
  confirmEmail: string;
};

export function useDeleteAccount() {
  return useMutation({
    mutationFn: async (body: DeleteAccountInput) => {
      const res = await authFetch(`/api/v1/auth/me/delete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const reply = await res.json().catch(() => ({}));
        throw new Error(reply.detail ?? reply.message ?? `HTTP ${res.status}`);
      }
    },
  });
}
